/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useRouter } from "vue-router";
import { LemmonModal } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";

const EMOJI_OPTIONS = ["🍋", "📊", "🌐", "🎯", "📋", "🚀", "💡", "🎨"];
const COLOR_OPTIONS: { value: string; class: string }[] = [
  { value: "amber", class: "bg-amber-400" },
  { value: "sky", class: "bg-sky-400" },
  { value: "emerald", class: "bg-emerald-400" },
  { value: "violet", class: "bg-violet-400" },
  { value: "rose", class: "bg-rose-400" },
  { value: "neutral", class: "bg-base-content/35" },
];

export const ProjectCreateModal = defineComponent<{
  onClose: () => void;
  onCreated?: () => void;
}>({
  name: "ProjectCreateModal",
  props: ["onClose", "onCreated"],
  setup(props) {
    const trpc = useTrpc();
    const router = useRouter();
    const name = ref("");
    const description = ref("");
    const icon = ref("🍋");
    const color = ref("amber");
    const creating = ref(false);
    const canCreate = computed(
      () => name.value.trim().length > 0 && !creating.value,
    );

    const handleCreate = async () => {
      if (!canCreate.value) return;
      creating.value = true;
      try {
        const project = (await trpc.intelligence.createProject.mutate({
          name: name.value.trim(),
          icon: icon.value,
          color: color.value,
          description: description.value.trim() || undefined,
        })) as { id: string };
        props.onClose();
        await props.onCreated?.();
        router.push(`/app/intelligence/projects/${project.id}/settings`);
      } finally {
        creating.value = false;
      }
    };

    return () => (
      <LemmonModal
        title="New project"
        onClose={props.onClose}
        style="max-width: 520px;"
        renderButtons={() => (
          <div class="flex items-center justify-end gap-2 border-t border-base-content/10 bg-base-100 px-5 py-3">
            <button onClick={props.onClose} class="btn btn-sm btn-ghost">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={!canCreate.value}
              class="btn btn-sm btn-primary gap-1.5"
            >
              <span class="material-symbols-outlined" style="font-size:14px">
                add
              </span>
              Create
            </button>
          </div>
        )}
      >
        <div class="space-y-5 px-5 py-5">
          <div>
            <label class="mb-1 block text-xs font-medium uppercase tracking-wide text-base-content/40">
              Project name
            </label>
            <input
              type="text"
              value={name.value}
              onInput={(event) =>
                (name.value = (event.target as HTMLInputElement).value)
              }
              placeholder="Summer campaign"
              class="input input-bordered input-sm w-full bg-base-100"
              autofocus
            />
          </div>

          <div>
            <label class="mb-1 block text-xs font-medium uppercase tracking-wide text-base-content/40">
              Description
            </label>
            <textarea
              value={description.value}
              onInput={(event) =>
                (description.value = (
                  event.target as HTMLTextAreaElement
                ).value)
              }
              rows={3}
              placeholder="What should this workspace focus on?"
              class="textarea textarea-bordered min-h-[88px] w-full resize-none bg-base-100 text-sm"
            />
          </div>

          <div>
            <div>
              <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-base-content/40">
                Icon
              </label>
              <div class="flex flex-wrap gap-1.5">
                {EMOJI_OPTIONS.map((option) => (
                  <button
                    key={option}
                    onClick={() => (icon.value = option)}
                    class={[
                      "flex h-8 w-8 items-center justify-center rounded-md border text-lg transition-colors hover:bg-base-content/5",
                      icon.value === option
                        ? "border-primary bg-primary/10"
                        : "border-base-content/10",
                    ].join(" ")}
                    title={option}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div class="mt-4">
              <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-base-content/40">
                Color
              </label>
              <div class="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => (color.value = option.value)}
                    class={[
                      "h-6 w-6 rounded-full transition-opacity",
                      option.class,
                      color.value === option.value
                        ? "ring-2 ring-base-content/35 ring-offset-2 ring-offset-base-100"
                        : "opacity-65 hover:opacity-100",
                    ].join(" ")}
                    title={option.value}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
